import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  Shield,
  CheckCircle,
  ArrowBack,
  HourglassEmpty,
  InfoOutlined,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { UserStatus } from '../../types';

// TossCert SDK 타입 선언
declare global {
  interface Window {
    TossCert: new (options: { txId: string }) => {
      preparePopup: () => void;
      open: (options: {
        onSuccess: () => void;
        onFail: (err: unknown) => void;
      }) => void;
    };
  }
}

type Step = 'guide' | 'pending' | 'verified' | 'success' | 'success-updated' | 'error';

const TOSS_CERT_SDK_URL = 'https://cert.toss.im/sdk/v2/tosscert.js';
const TIMEOUT_MS = 10 * 60 * 1000; // 10분
const IS_MOCK = process.env.REACT_APP_MOCK_IDENTITY_VERIFICATION === 'true';

const IdentityVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();

  const [step, setStep] = useState<Step>('guide');
  const [sdkLoaded, setSdkLoaded] = useState(IS_MOCK);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [wasUpdated, setWasUpdated] = useState(false);

  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 새로고침 시 이미 PENDING이면 대시보드(심사 대기 화면)로 리다이렉트
  // 플로우 중 상태 변경 시에는 동작하지 않도록 마운트 시 1회만 체크
  useEffect(() => {
    if (user?.status === UserStatus.PENDING) {
      navigate('/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TossCert SDK 동적 로드 (mock 모드에서는 건너뜀)
  useEffect(() => {
    if (IS_MOCK) return;
    if (document.querySelector(`script[src="${TOSS_CERT_SDK_URL}"]`)) {
      setSdkLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = TOSS_CERT_SDK_URL;
    script.async = true;
    script.onload = () => setSdkLoaded(true);
    script.onerror = () => {
      showError('본인인증 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    };
    document.head.appendChild(script);
  }, []);

  // 타임아웃 정리
  const clearTimeoutTimer = useCallback(() => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearTimeoutTimer();
  }, [clearTimeoutTimer]);


  const showError = (msg: string) => {
    setSnackbarMsg(msg);
    setSnackbarOpen(true);
  };

  // TossCert 성공 콜백 → 본인인증 결과 처리 → 프로필 등록 API 호출
  const handleVerificationSuccess = useCallback(async (txId: string) => {
    clearTimeoutTimer();
    setIsProcessing(true);

    try {
      // 1. 본인인증 결과 처리
      const { wasUpdated: updated } = await apiClient.completeIdentityVerification(txId);

      // 2. 본인인증 완료 후 프로필 제출 → PENDING 전환
      await apiClient.submitProfile();

      // 3. AuthContext + localStorage에 PENDING 상태 반영 (새로고침 시 유지)
      updateUser({ status: UserStatus.PENDING });

      setWasUpdated(updated);
      setStep('verified');
    } catch (e: any) {
      const errorCode = e?.errorCode ?? e?.code ?? '';
      if (errorCode === 'GTH_4027') {
        // 이미 PENDING 상태 → 성공으로 처리
        setStep('success');
      } else {
        if (errorCode === 'DUPLICATE_PHONE') {
          showError('이 번호로 이미 가입된 계정이 있어요. 고객센터에 문의해주세요.');
        } else if (errorCode === 'GTH_4054') {
          showError('본인인증이 완료되지 않았습니다. 다시 시도해 주세요.');
        } else if (errorCode === 'GTH_4042') {
          showError('프로필 정보를 찾을 수 없습니다. 처음부터 다시 시도해 주세요.');
        } else {
          showError('인증 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
        }
        setStep('guide');
      }
    } finally {
      setIsProcessing(false);
    }
  }, [clearTimeoutTimer]);

  const handleStartVerification = async () => {
    if (IS_MOCK) {
      // Mock 모드: SDK 없이 전체 플로우 시뮬레이션
      try {
        const { txId } = await apiClient.startIdentityVerification();
        setStep('pending');
        setTimedOut(false);
        // 2초 딜레이 후 자동 성공 처리
        await new Promise<void>((resolve) => setTimeout(resolve, 2000));
        await handleVerificationSuccess(txId);
      } catch (e) {
        showError('본인인증 시작에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        setStep('guide');
      }
      return;
    }

    if (!sdkLoaded || !window.TossCert) {
      showError('본인인증 서비스가 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    // preparePopup은 반드시 await 이전에 동기적으로 호출해야 팝업이 차단되지 않음
    const tossCert = new window.TossCert({ txId: '' });
    tossCert.preparePopup();

    try {
      const { txId } = await apiClient.startIdentityVerification();

      setStep('pending');
      setTimedOut(false);

      // 10분 타임아웃
      timeoutTimerRef.current = setTimeout(() => {
        setTimedOut(true);
        showError('인증 시간이 초과됐습니다. 다시 시도해 주세요.');
      }, TIMEOUT_MS);

      const certInstance = new window.TossCert({ txId });
      certInstance.open({
        onSuccess: () => handleVerificationSuccess(txId),
        onFail: () => {
          clearTimeoutTimer();
          showError('본인인증이 취소됐습니다. 다시 시도해 주세요.');
          setStep('guide');
        },
      });
    } catch (e) {
      showError('본인인증 시작에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      setStep('guide');
    }
  };

  const handleRetry = () => {
    clearTimeoutTimer();
    setTimedOut(false);
    setStep('guide');
  };

  // ─── Screen A: 안내 ──────────────────────────────────────────────────────────
  const renderGuide = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4, px: 2 }}>
      <Shield sx={{ fontSize: 80, color: '#C9A86C', mt: 2 }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontFamily: 'Playfair Display, serif',
            fontWeight: 700,
            color: '#0D1F3C',
            fontSize: '1.5rem',
            mb: 1,
          }}
        >
          마지막 단계예요!
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '1.125rem',
            color: '#4A5568',
            lineHeight: 1.7,
          }}
        >
          본인인증을 완료하면 심사가 시작됩니다
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          bgcolor: '#F0F3F8',
          border: '1px solid #D4BFA0',
          borderRadius: 2,
          p: 3,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Typography
          sx={{ fontFamily: 'Pretendard, sans-serif', fontSize: '1rem', color: '#4A5568', lineHeight: 1.8 }}
        >
          인증을 통해 확인되는 정보:
        </Typography>
        {[
          '실명',
          '생년월일',
          '성별',
        ].map((item) => (
          <Typography
            key={item}
            sx={{
              fontFamily: 'Pretendard, sans-serif',
              fontSize: '1rem',
              color: '#4A5568',
              lineHeight: 1.8,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
            }}
          >
            <CheckCircle sx={{ fontSize: '1rem', color: '#C9A86C', mt: '3px', flexShrink: 0 }} />
            {item}
          </Typography>
        ))}
        <Typography
          sx={{
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '0.9rem',
            color: '#718096',
            mt: 2,
            lineHeight: 1.7,
          }}
        >
          허위 프로필을 방지하고, 정확한 나이·성별 기반의
          신뢰할 수 있는 매칭을 제공하기 위해 본인인증이 필요합니다.
        </Typography>
      </Paper>

      <Button
        variant="contained"
        fullWidth
        onClick={handleStartVerification}
        disabled={!sdkLoaded}
        sx={{
          bgcolor: '#0064FF',
          color: '#FFFFFF',
          fontSize: '1.125rem',
          fontWeight: 700,
          fontFamily: 'Pretendard, sans-serif',
          height: 56,
          borderRadius: 2,
          maxWidth: 400,
          '&:hover': { bgcolor: '#0052CC' },
          '&:disabled': { bgcolor: '#B0C4DE', color: '#FFFFFF' },
        }}
      >
        {sdkLoaded ? '토스로 본인인증하기' : '로딩 중...'}
      </Button>
    </Box>
  );

  // ─── Screen B: 앱 전환 대기 ──────────────────────────────────────────────────
  const renderPending = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, py: 6, px: 2 }}>
      {isProcessing ? (
        <>
          <CircularProgress size={80} thickness={3} sx={{ color: '#0064FF' }} />
          <Typography
            sx={{
              fontSize: '1.125rem',
              color: '#0D1F3C',
              textAlign: 'center',
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: 600,
            }}
          >
            인증 결과를 처리하고 있어요...
          </Typography>
        </>
      ) : timedOut ? (
        <>
          <HourglassEmpty sx={{ fontSize: 80, color: '#C9A86C' }} />
          <Typography
            sx={{
              fontSize: '1.125rem',
              color: '#0D1F3C',
              textAlign: 'center',
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: 600,
            }}
          >
            인증 시간이 초과됐어요
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={80} thickness={3} sx={{ color: '#0064FF' }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.125rem',
                color: '#0D1F3C',
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: 600,
                lineHeight: 1.6,
                mb: 1,
              }}
            >
              토스 앱에서 인증을 완료해 주세요
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#718096', fontFamily: 'Pretendard, sans-serif' }}>
              최대 10분 내에 인증을 완료해 주세요
            </Typography>
          </Box>
        </>
      )}

      {!isProcessing && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 400 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRetry}
            sx={{
              borderColor: '#0064FF',
              color: '#0064FF',
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: 'Pretendard, sans-serif',
              height: 56,
              borderRadius: 2,
            }}
          >
            처음으로 돌아가기
          </Button>
        </Box>
      )}
    </Box>
  );

  // ─── Screen B-2: 본인인증 완료 ──────────────────────────────────────────────
  const renderVerified = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 6, px: 2 }}>
      <CheckCircle sx={{ fontSize: 72, color: '#C9A86C' }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontFamily: 'Playfair Display, serif',
            fontWeight: 700,
            color: '#0D1F3C',
            fontSize: '1.75rem',
            mb: 1,
          }}
        >
          본인인증이 완료되었어요!
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '1rem',
            color: '#718096',
            lineHeight: 1.6,
          }}
        >
          프로필과 함께 심사가 접수되었습니다
        </Typography>
      </Box>

      {wasUpdated && (
        <Paper
          elevation={0}
          sx={{
            bgcolor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: 2,
            p: 3,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <InfoOutlined sx={{ color: '#3B82F6', mt: 0.25, flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: 'Pretendard, sans-serif',
              fontSize: '1rem',
              color: '#0D1F3C',
              lineHeight: 1.7,
            }}
          >
            본인인증 결과에 따라 일부 정보가 업데이트되었어요.
            정확한 매칭을 위해 실제 정보를 사용합니다.
          </Typography>
        </Paper>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={() => navigate('/dashboard', { replace: true })}
        sx={{
          bgcolor: '#0D1F3C',
          color: '#FFFFFF',
          fontSize: '1.125rem',
          fontWeight: 700,
          fontFamily: 'Pretendard, sans-serif',
          height: 56,
          borderRadius: 2,
          maxWidth: 400,
          '&:hover': { bgcolor: '#1A3357' },
        }}
      >
        심사 현황 확인하기
      </Button>
    </Box>
  );

  // ─── Screen C-1: 완료 ────────────────────────────────────────────────────────
  const renderSuccess = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 6, px: 2 }}>
      <CheckCircle sx={{ fontSize: 72, color: '#C9A86C' }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontFamily: 'Playfair Display, serif',
            fontWeight: 700,
            color: '#0D1F3C',
            fontSize: '1.75rem',
            mb: 1,
          }}
        >
          심사가 접수되었어요!
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '1rem',
            color: '#718096',
            lineHeight: 1.6,
          }}
        >
          심사 결과는 알림으로 알려드릴게요
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          bgcolor: '#F0F3F8',
          border: '1px solid #D4BFA0',
          borderRadius: 2,
          p: 3,
          width: '100%',
          maxWidth: 400,
        }}
      >
        {['본인인증 완료', '프로필 작성 완료', '심사 접수 완료'].map((item) => (
          <Typography
            key={item}
            sx={{
              fontFamily: 'Pretendard, sans-serif',
              fontSize: '1.125rem',
              color: '#4A5568',
              lineHeight: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CheckCircle sx={{ fontSize: '1.125rem', color: '#C9A86C' }} />
            {item}
          </Typography>
        ))}
      </Paper>

      <Button
        variant="contained"
        fullWidth
        onClick={() => navigate('/dashboard', { replace: true })}
        sx={{
          bgcolor: '#0D1F3C',
          color: '#FFFFFF',
          fontSize: '1.125rem',
          fontWeight: 700,
          fontFamily: 'Pretendard, sans-serif',
          height: 56,
          borderRadius: 2,
          maxWidth: 400,
          '&:hover': { bgcolor: '#1A3357' },
        }}
      >
        심사 현황 확인하기
      </Button>
    </Box>
  );

  // ─── Screen C-2: 완료 + 정보 업데이트 ───────────────────────────────────────
  const renderSuccessUpdated = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 6, px: 2 }}>
      <CheckCircle sx={{ fontSize: 72, color: '#C9A86C' }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontFamily: 'Playfair Display, serif',
            fontWeight: 700,
            color: '#0D1F3C',
            fontSize: '1.75rem',
            mb: 1,
          }}
        >
          심사가 접수되었어요!
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '1rem',
            color: '#718096',
            lineHeight: 1.6,
          }}
        >
          심사 결과는 알림으로 알려드릴게요
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          bgcolor: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 2,
          p: 3,
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        <InfoOutlined sx={{ color: '#3B82F6', mt: 0.25, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '1rem',
            color: '#0D1F3C',
            lineHeight: 1.7,
          }}
        >
          본인인증 결과에 따라 일부 정보가 업데이트되었어요.
          정확한 매칭을 위해 실제 정보를 사용합니다.
        </Typography>
      </Paper>

      <Button
        variant="contained"
        fullWidth
        onClick={() => navigate('/dashboard', { replace: true })}
        sx={{
          bgcolor: '#0D1F3C',
          color: '#FFFFFF',
          fontSize: '1.125rem',
          fontWeight: 700,
          fontFamily: 'Pretendard, sans-serif',
          height: 56,
          borderRadius: 2,
          maxWidth: 400,
          '&:hover': { bgcolor: '#1A3357' },
        }}
      >
        심사 현황 확인하기
      </Button>
    </Box>
  );

  const renderStep = () => {
    switch (step) {
      case 'guide':       return renderGuide();
      case 'pending':     return renderPending();
      case 'verified':    return renderVerified();
      case 'success':     return renderSuccess();
      case 'success-updated': return renderSuccessUpdated();
      default:            return renderGuide();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FFFFFF' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#0D1F3C', borderRadius: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{ color: '#FFFFFF', mr: 2 }}
            disabled={isProcessing}
          >
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontFamily: 'Playfair Display, serif', color: '#FFFFFF', fontWeight: 700, flexGrow: 1 }}
          >
            본인인증
          </Typography>
        </Toolbar>
      </AppBar>

      {IS_MOCK && (
        <Box
          sx={{
            bgcolor: '#FFF3CD',
            borderBottom: '1px solid #FFD700',
            py: 0.5,
            px: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontFamily: 'Pretendard, sans-serif',
              color: '#856404',
              fontWeight: 600,
            }}
          >
            🔧 Mock Mode
          </Typography>
        </Box>
      )}

      <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, py: 2 }}>
        {renderStep()}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setSnackbarOpen(false)}
          sx={{ fontSize: '1rem', fontFamily: 'Pretendard, sans-serif' }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IdentityVerificationPage;
